import { useMemo, useRef, useState } from "react";
import { useApolloClient, useMutation, useQuery } from "@apollo/client/react";
import { CheckCircle2, TriangleAlert, UploadIcon } from "lucide-react";
import { toast } from "sonner";
import {
    Button,
    Field,
    FieldGroup,
    FieldLabel,
    FieldSet,
    Grid,
    Input,
    ListCard,
    ListCardRow,
    Mono,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Skeleton,
    Text,
} from "@athena/ui";
import {
    OrganizationRosterDocument,
    OrganizationRosterCountDocument,
    SendTestAutoEnrollEmailDocument,
    SetAutoEnrollEmailSubjectsDocument,
} from "@/graphql/organizations.generated";
import { reportQueryFailure } from "@/lib/queryErrors";
import { AUTO_ENROLL_TEMPLATE, parseAutoEnrollCsv, type AutoEnrollCsvRow } from "@/lib/autoEnrollCsv";
import {
    clearAutoEnrollRun,
    MAX_BATCH,
    setAutoEnrollUpload,
    startAutoEnrollRun,
    useAutoEnrollRun,
} from "@/hooks/useAutoEnrollRun";
import { BrandsDocument } from "@/graphql/brands.generated";

/**
 * Auto-enrollment for one organization.
 *
 * Bulk-enrolls people from a CSV into this org and, per row, any of its
 * sub-orgs. Enrolling a sub-org always also grants parent-org membership, so a
 * person can never end up in a chapter without being in the parent.
 *
 * The file is parsed and checked here before anything is sent: the API rejects
 * the *whole* request if one sub-org id is wrong, so a mistyped chapter name has
 * to be caught client-side or it fails every row in that batch.
 */

interface AutoEnrollOrg {
    id: string;
    name: string;
    subOrganizations: { id: string; name: string }[];
    autoEnrollEmailSubjects: { brand: string; subject: string }[];
}

/**
 * Roster rows rendered in the preview box below the upload.
 *
 * Enough to see an upload landed and to scroll a little; far short of the 8,635-row roster the
 * largest organization has. The counts beside it are the real totals.
 */
const ROSTER_PREVIEW_ROWS = 200;

/**
 * How many *valid* rows the preview shows as reassurance before it just reports a count.
 *
 * Every row that needs fixing is shown regardless — see `previewRows`. Twenty-five of thousands
 * of identical-looking good rows tells an operator nothing they do not already know from the
 * count beside it.
 */
const PREVIEW_LIMIT = 25;

/**
 * A ceiling on the problem rows too, because the DOM has one.
 *
 * Generous rather than tight: the point is that an operator can read every problem in their file,
 * and a file with more than this many is one to fix at the source rather than scroll through.
 */
const PROBLEM_ROW_LIMIT = 500;

/**
 * Two vocabularies, deliberately, and they answer different questions.
 *
 * An upload outcome says what this row *did*: enrolled, activated, duplicate, invalid, not sent.
 * A roster state says what this person *is* right now: invited, or active. The same person is
 * "enrolled" for the length of one upload and "invited" from then until they sign in.
 *
 * They used to be three words for one state — a row came back ENROLLED, appeared as "pending" in
 * the roster, and as "Invited" on the Users tab, with nothing saying those were the same person.
 * The display word is now "invited" everywhere, and the explanations below name the transition
 * rather than restating the label.
 */
const STATUS_MEANING: Record<string, string> = {
    // In progress — the client's own, since the server only answers for rows it has seen.
    QUEUED: "Waiting — read from the file and not sent yet",
    SENDING: "Enrolling — this batch is with the server now",
    // Upload outcomes.
    ENROLLED: "Invited — no account yet, and becomes Active when they first sign in",
    ACTIVATED: "Already had an account, so they joined the organization straight away — Active now",
    DUPLICATE: "Already on this roster — nothing changed, and nobody was emailed twice",
    INVALID: "Not enrolled — the row could not be used, so this person was not added",
    NOT_SENT: "Never reached the server — upload the same file again to finish these",
    // Roster states.
    PENDING: "Invited — on the roster with no account yet, until they first sign in",
    ACTIVE: "Active — has signed in, so a real user account exists",
};

/**
 * Both roster states, always, even when nobody on screen is in one of them.
 *
 * This listed only the statuses actually rendered, which is the right instinct for a legend and
 * the wrong one here: the question is what invited means *as opposed to* active, and a roster that
 * is entirely invitations — every roster, right after an upload — would show one line and answer
 * nothing. Two lines is not a long key.
 */
const ROSTER_STATES = [
    "PENDING",
    "ACTIVE",
] as const;

/**
 * The word a pill shows, where it differs from the status name.
 *
 * `PENDING` is the case: the API calls it pending, the Users tab has always called the same person
 * Invited, and a partner reading "pending" reasonably asks what is pending about it.
 */
const STATUS_LABEL: Record<string, string> = {
    PENDING: "invited",
    NOT_SENT: "not sent",
    QUEUED: "waiting",
    SENDING: "enrolling",
};

const STATUS_TONE: Record<string, string> = {
    // Neither an outcome nor a problem: grey for waiting, and the in-flight one carries the
    // accent so an operator can see where the run has reached at a glance down the column.
    QUEUED: "border-border bg-muted text-muted-foreground",
    SENDING: "border-secondary/30 bg-secondary/10 text-secondary",
    ENROLLED: "border-success/30 bg-success/10 text-success",
    ACTIVATED: "border-success/30 bg-success/10 text-success",
    DUPLICATE: "border-border bg-muted text-muted-foreground",
    INVALID: "border-destructive/30 bg-destructive/10 text-destructive",
    // Not a rejection — the server never saw these rows, and they are the operator's next action.
    NOT_SENT: "border-warning/30 bg-warning/10 text-warning",
    ACTIVE: "border-success/30 bg-success/10 text-success",
    PENDING: "border-border bg-muted text-muted-foreground",
};

function StatusPill({ status }: { status: string }) {
    return (
        <span
            title={STATUS_MEANING[status]}
            className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${
                STATUS_TONE[status] ?? "border-border bg-muted text-muted-foreground"
            }`}
        >
            {STATUS_LABEL[status] ?? status.toLowerCase()}
        </span>
    );
}

/**
 * One status column for the whole run: what's wrong with the row, or what the
 * server did with it. A second table for results meant reading the same rows twice.
 */
function RowStatus({ row, result }: { row: AutoEnrollCsvRow; result?: { status: string; message?: string | null } }) {
    if (row.error) {
        return (
            <span className="text-destructive inline-flex items-center gap-1 text-xs">
                <TriangleAlert className="h-3.5 w-3.5" />
                {row.error}
            </span>
        );
    }
    if (result) {
        return (
            <span className="inline-flex items-center gap-2">
                <StatusPill status={result.status} />
                <span className="text-muted-foreground text-xs">
                    {result.message ?? STATUS_MEANING[result.status] ?? ""}
                </span>
            </span>
        );
    }
    return (
        <span className="text-success inline-flex items-center gap-1 text-xs">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Ready
        </span>
    );
}

export function OrganizationAutoEnroll({ org }: { org: AutoEnrollOrg }) {
    const fileInput = useRef<HTMLInputElement>(null);
    const client = useApolloClient();
    /*
     * The upload itself lives outside this component — see `useAutoEnrollRun`. It has to outlive
     * the page, because an 8,500 row file is about ten minutes of sending and switching to the
     * Users tab to watch the count move used to cancel the run and throw away its results.
     */
    const run = useAutoEnrollRun(org.id);
    const fileName = run?.fileName ?? null;
    /** Row status per email: waiting and enrolling while it runs, then the server's own verdict. */
    const results = run?.statuses;
    const enrolling = run?.running ?? false;
    const [
        defaultBrand,
        setDefaultBrand,
    ] = useState<string>("");
    const [
        subjects,
        setSubjects,
    ] = useState<Record<string, string>>(() =>
        Object.fromEntries(
            org.autoEnrollEmailSubjects.map((entry) => [
                entry.brand,
                entry.subject,
            ]),
        ),
    );
    const [
        testEmail,
        setTestEmail,
    ] = useState<string>("");
    const [
        testBrand,
        setTestBrand,
    ] = useState<string>("RELEASE_U");

    const roster = useQuery(OrganizationRosterDocument, { variables: { organizationId: org.id } });
    const { data: brandsData } = useQuery(BrandsDocument);
    const brands = brandsData?.brands;

    const [
        saveSubjects,
        { loading: savingSubjects },
    ] = useMutation(SetAutoEnrollEmailSubjectsDocument);
    const [
        sendTest,
        { loading: sendingTest },
    ] = useMutation(SendTestAutoEnrollEmailDocument);

    const rows = useMemo(() => run?.rows ?? [], [run]);
    const valid = useMemo(() => rows.filter((row) => !row.error), [rows]);
    const invalid = useMemo(() => rows.filter((row) => row.error), [rows]);

    const readFile = async (file: File) => {
        const text = await file.text();
        const result = parseAutoEnrollCsv(text, org.subOrganizations);
        setAutoEnrollUpload(org.id, file.name, result.rows);
        if (result.fatal) toast.error(result.fatal);
    };

    const clearFile = () => {
        clearAutoEnrollRun(org.id);
        // Without this the same file can't be re-picked after a fix — the input
        // fires no change event when the value is unchanged.
        if (fileInput.current) fileInput.current.value = "";
    };

    const downloadTemplate = () => {
        const url = URL.createObjectURL(new Blob([AUTO_ENROLL_TEMPLATE], { type: "text/csv" }));
        const link = document.createElement("a");
        link.href = url;
        link.download = "auto-enroll-template.csv";
        link.click();
        URL.revokeObjectURL(url);
    };

    /**
     * Hands the run to the store and returns.
     *
     * The batching, retries and per-row statuses live in `useAutoEnrollRun` rather than here, so
     * the upload is not tied to this component's lifetime. Navigating away while 8,500 rows are
     * going out used to abort the request in flight and discard every result with it.
     */
    const submit = () => {
        if (valid.length === 0) return;
        void startAutoEnrollRun(client, { organizationId: org.id, defaultBrand });
    };

    const handleSaveSubjects = async () => {
        if (!brands) return;
        try {
            await saveSubjects({
                variables: {
                    input: {
                        organizationId: org.id,
                        // The whole set every time: a brand left blank is cleared,
                        // which is how an override is removed.
                        subjects: brands.map((brand) => ({
                            brand: brand.slug,
                            subject: subjects[brand.slug] ?? "",
                        })),
                    },
                },
            });
            toast.success("Subject lines saved.");
        } catch (error) {
            reportQueryFailure(error, "Could not save the subject lines");
        }
    };

    const handleSendTest = async () => {
        try {
            const response = await sendTest({
                variables: {
                    input: { organizationId: org.id, email: testEmail.trim(), brand: testBrand },
                },
            });
            const result = response.data?.sendTestAutoEnrollEmail;
            if (result?.ok) toast.success(`${result.message} Subject: "${result.subject}"`);
            else toast.error(result?.message ?? "Could not send the sample");
        } catch (error) {
            reportQueryFailure(error, "Could not send the sample");
        }
    };

    /*
     * Every row that needs fixing, then good rows up to the reassurance limit.
     *
     * This was `[...invalid, ...valid].slice(0, 25)`, which meant a file with more than 25
     * problems showed only some of them — and an 8,500-row upload is exactly where an operator
     * needs the full list, because they are going back to the source spreadsheet to fix it. The
     * good rows are capped instead: they all look the same, and the count beside the table already
     * says how many there are.
     */
    const shownInvalid = Math.min(invalid.length, PROBLEM_ROW_LIMIT);
    const previewRows = useMemo(
        () => [
            ...invalid.slice(0, PROBLEM_ROW_LIMIT),
            ...valid.slice(0, PREVIEW_LIMIT),
        ],
        [
            invalid,
            valid,
        ],
    );

    const rosterEntries = roster.data?.organizationRoster ?? [];
    /*
     * Both counts come from the database, not from the rows on screen.
     *
     * They used to be `rosterEntries.filter(...).length` and `rosterEntries.length`, and that array
     * is one page of a capped query — so every organization past the cap reported exactly 1,000.
     * Four production organizations are: Beta Theta Pi has 5,094 invited and this tab said 1,000,
     * which also made it disagree with the Users tab about the same people.
     */
    const { data: pendingCountData } = useQuery(OrganizationRosterCountDocument, {
        variables: { organizationId: org.id, status: "PENDING" },
    });
    const { data: totalCountData } = useQuery(OrganizationRosterCountDocument, {
        variables: { organizationId: org.id },
    });
    const pendingCount = pendingCountData?.organizationRosterCount ?? 0;
    const rosterTotal = totalCountData?.organizationRosterCount ?? rosterEntries.length;
    /*
     * Rendered rows are capped, and now the counts above them genuinely are not.
     *
     * This list sits in a 288px scroll box and existed to confirm an upload landed. Rendering the
     * whole roster to do that put 8,635 table rows in the DOM on the largest organization, which
     * made opening this tab slow for a list nobody scrolls to the end of. Invitees sort first
     * because they are what an upload just created and what this tab is about; the Users tab lists
     * and searches everybody properly.
     */
    const visibleRoster = useMemo(
        () =>
            [...rosterEntries]
                .sort((a, b) => Number(b.status === "PENDING") - Number(a.status === "PENDING"))
                .slice(0, ROSTER_PREVIEW_ROWS),
        [rosterEntries],
    );

    return (
        <>
            <Grid cols={1}>
                <ListCard
                    title="Upload Users"
                    description="Import members from a CSV to auto-enroll them in this organization."
                >
                    <ListCardRow className="flex flex-col items-stretch gap-4">
                        <div className="bg-muted/50 border-border flex w-full flex-wrap items-center justify-between gap-4 rounded-xl border-2 border-dashed px-5 py-4">
                            <div className="flex items-center gap-4">
                                <div className="border-border bg-background flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border shadow-sm">
                                    <UploadIcon className="text-muted-foreground h-5 w-5" />
                                </div>
                                <div className="flex flex-col gap-0.5">
                                    <Text weight="semibold">{fileName ?? "Choose a CSV to upload"}</Text>
                                    <Text size="sm" muted>
                                        Columns: email, first name, last name, sub-org (optional), brand (optional).{" "}
                                        <button
                                            type="button"
                                            onClick={downloadTemplate}
                                            className="text-text-link cursor-pointer font-bold hover:underline"
                                        >
                                            Download template
                                        </button>
                                    </Text>
                                </div>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                                {run ? (
                                    <Button size="sm" variant="outline" className="w-auto" onClick={clearFile}>
                                        Clear
                                    </Button>
                                ) : null}
                                <Button
                                    size="sm"
                                    className="flex w-auto items-center gap-1.5"
                                    onClick={() => fileInput.current?.click()}
                                >
                                    <UploadIcon className="h-3.5 w-3.5" />
                                    {fileName ? "Choose another" : "Upload CSV File"}
                                </Button>
                            </div>
                            <input
                                ref={fileInput}
                                type="file"
                                accept=".csv,text/csv"
                                className="sr-only"
                                onChange={(event) => {
                                    const file = event.target.files?.[0];
                                    if (file) void readFile(file);
                                }}
                            />
                        </div>

                        {rows.length > 0 ? (
                            <>
                                <div className="flex flex-wrap items-center gap-3">
                                    {/* Two plain strings rather than one interpolated sentence: a
                                        count split across <span>s is harder to read out loud, and
                                        harder to assert on. */}
                                    <Text size="sm" weight="semibold">{`${valid.length} ready to enroll`}</Text>
                                    {invalid.length > 0 ? (
                                        <Text size="sm" weight="semibold" className="text-destructive">
                                            {`${invalid.length} need fixing`}
                                        </Text>
                                    ) : null}
                                    <div className="ml-auto flex items-center gap-2">
                                        <Text size="sm" muted>
                                            Brand for rows without one
                                        </Text>
                                        <Select
                                            value={defaultBrand}
                                            onValueChange={(value) => setDefaultBrand(value as string)}
                                        >
                                            <SelectTrigger className="w-44">
                                                <SelectValue placeholder="Default (Release U)" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {brands?.map((brand) => (
                                                    <SelectItem key={brand.slug} value={brand.slug}>
                                                        {brand.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        <Button
                                            size="sm"
                                            className="w-auto"
                                            disabled={valid.length === 0 || enrolling}
                                            onClick={() => void submit()}
                                        >
                                            {enrolling ? "Enrolling…" : `Enroll ${valid.length}`}
                                        </Button>
                                    </div>
                                </div>

                                <div className="border-border overflow-x-auto rounded-lg border">
                                    <table className="w-full text-sm">
                                        <thead className="bg-muted/50 text-muted-foreground text-xs">
                                            <tr>
                                                <th className="px-3 py-2 text-left font-medium">Line</th>
                                                <th className="px-3 py-2 text-left font-medium">Email</th>
                                                <th className="px-3 py-2 text-left font-medium">Name</th>
                                                {/*
                                                 * Shown even when the file has no phone column, so a
                                                 * row of dashes says the numbers are not being read.
                                                 * Silently omitting the column is how a partner's
                                                 * whole roster of numbers went missing unnoticed.
                                                 */}
                                                <th className="px-3 py-2 text-left font-medium">Phone</th>
                                                <th className="px-3 py-2 text-left font-medium">Sub-orgs</th>
                                                <th className="px-3 py-2 text-left font-medium">Brand</th>
                                                <th className="px-3 py-2 text-left font-medium">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {previewRows.map((row) => (
                                                <tr key={row.line} className="border-border border-t">
                                                    <td className="text-muted-foreground px-3 py-2">{row.line}</td>
                                                    <td className="px-3 py-2">{row.email || "—"}</td>
                                                    <td className="px-3 py-2">
                                                        {[
                                                            row.firstName,
                                                            row.lastName,
                                                        ]
                                                            .filter(Boolean)
                                                            .join(" ") || "—"}
                                                    </td>
                                                    <td className="text-muted-foreground px-3 py-2">
                                                        {row.phoneNumber ?? "—"}
                                                    </td>
                                                    <td className="text-muted-foreground px-3 py-2">
                                                        {row.subOrganizationNames.join(", ") || "Parent org only"}
                                                    </td>
                                                    <td className="text-muted-foreground px-3 py-2">
                                                        {row.brand ?? defaultBrand ?? "—"}
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <RowStatus row={row} result={results?.get(row.line)} />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {rows.length > previewRows.length ? (
                                    <Text size="sm" muted>
                                        Showing {previewRows.length.toLocaleString()} of {rows.length.toLocaleString()}{" "}
                                        rows
                                        {shownInvalid === invalid.length && invalid.length > 0
                                            ? `, including all ${invalid.length.toLocaleString()} that need fixing`
                                            : shownInvalid < invalid.length
                                              ? `, including ${shownInvalid.toLocaleString()} of the ${invalid.length.toLocaleString()} that need fixing`
                                              : ""}
                                        . All {valid.length.toLocaleString()} valid rows are enrolled, in batches of{" "}
                                        {MAX_BATCH}.
                                    </Text>
                                ) : null}
                            </>
                        ) : null}
                    </ListCardRow>
                </ListCard>

                <ListCard
                    title="Auto-Enrollment Email"
                    description="Subject line new members receive, per brand. Leave one blank to use the default wording."
                >
                    <ListCardRow className="flex flex-col items-stretch gap-3">
                        <FieldSet className="w-full max-w-none">
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                                {brands?.map((brand) => (
                                    <FieldGroup key={brand.slug}>
                                        <Field>
                                            <FieldLabel htmlFor={`subject-${brand.slug}`}>
                                                {brand.name} — email subject
                                            </FieldLabel>
                                            <Input
                                                id={`subject-${brand.slug}`}
                                                type="text"
                                                value={subjects[brand.slug] ?? ""}
                                                placeholder={`You've been invited to join ${org.name}`}
                                                onChange={(event) =>
                                                    setSubjects((current) => ({
                                                        ...current,
                                                        [brand.slug]: event.target.value,
                                                    }))
                                                }
                                            />
                                            <Mono size="2xs" className="ml-1">
                                                Blank = default wording
                                            </Mono>
                                        </Field>
                                    </FieldGroup>
                                ))}
                            </div>
                        </FieldSet>
                        <Button
                            size="sm"
                            className="w-auto self-start"
                            disabled={savingSubjects}
                            onClick={() => void handleSaveSubjects()}
                        >
                            {savingSubjects ? "Saving…" : "Save subject lines"}
                        </Button>
                    </ListCardRow>
                </ListCard>
            </Grid>

            <Grid cols={2} className="pt-4">
                <ListCard
                    title="Test Auto-Enrollment Email"
                    description="Send a sample to confirm everything looks right before members get it. Enrolls nobody."
                >
                    <ListCardRow className="flex flex-col items-stretch gap-3">
                        <FieldSet className="w-full max-w-none">
                            <FieldGroup>
                                <Field>
                                    <FieldLabel htmlFor="test-email">Email address</FieldLabel>
                                    <Input
                                        id="test-email"
                                        type="email"
                                        value={testEmail}
                                        placeholder="you@organization.org"
                                        onChange={(event) => setTestEmail(event.target.value)}
                                    />
                                    <Mono size="2xs" className="ml-1">
                                        A copy of the welcome email goes to this address.
                                    </Mono>
                                </Field>
                                <Field>
                                    <FieldLabel htmlFor="test-brand">Brand</FieldLabel>
                                    <Select value={testBrand} onValueChange={(value) => setTestBrand(value as string)}>
                                        <SelectTrigger id="test-brand" className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {brands?.map((brand) => (
                                                <SelectItem key={brand.slug} value={brand.slug}>
                                                    {brand.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </Field>
                            </FieldGroup>
                        </FieldSet>
                        <Button
                            className="w-full max-w-none"
                            disabled={!testEmail.trim() || sendingTest}
                            onClick={() => void handleSendTest()}
                        >
                            {sendingTest ? "Sending…" : "Send test email"}
                        </Button>
                    </ListCardRow>
                </ListCard>

                <ListCard
                    title="Roster"
                    description="Everyone in this organization, including people who haven't signed in yet."
                >
                    <ListCardRow className="flex flex-col items-stretch gap-3">
                        {roster.loading ? (
                            <Skeleton className="h-24 w-full rounded-lg" />
                        ) : rosterEntries.length === 0 ? (
                            <Text size="sm" muted>
                                Nobody here yet. Upload a CSV to enroll people.
                            </Text>
                        ) : (
                            <>
                                <div className="flex flex-wrap items-center gap-3">
                                    <Text size="sm" weight="semibold">{`${rosterTotal.toLocaleString()} total`}</Text>
                                    {pendingCount > 0 ? (
                                        <Text
                                            size="sm"
                                            muted
                                        >{`${pendingCount.toLocaleString()} awaiting first sign-in`}</Text>
                                    ) : null}
                                    {rosterEntries.length > visibleRoster.length ? (
                                        <Text size="sm" muted>
                                            {`showing ${visibleRoster.length} — the Users tab lists everyone`}
                                        </Text>
                                    ) : null}
                                </div>
                                <div className="border-border max-h-72 overflow-auto rounded-lg border">
                                    <table className="w-full text-sm">
                                        <thead className="bg-muted/50 text-muted-foreground text-xs">
                                            <tr>
                                                <th className="px-3 py-2 text-left font-medium">Person</th>
                                                <th className="px-3 py-2 text-left font-medium">Status</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {visibleRoster.map((entry) => (
                                                <tr
                                                    key={`${entry.status}-${entry.email}`}
                                                    className="border-border border-t"
                                                >
                                                    <td className="px-3 py-2">
                                                        <Text className="text-sm">
                                                            {[
                                                                entry.firstName,
                                                                entry.lastName,
                                                            ]
                                                                .filter(Boolean)
                                                                .join(" ") || entry.email}
                                                        </Text>
                                                        <Text className="text-muted-foreground text-xs">
                                                            {entry.email}
                                                        </Text>
                                                    </td>
                                                    <td className="px-3 py-2">
                                                        <StatusPill status={entry.status} />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                {/*
                                 * The key. Invited against active is the question people actually
                                 * ask — one of these has an account and one does not, and the pill
                                 * on its own never said which.
                                 */}
                                <Text size="sm" muted className="font-medium">
                                    What these mean
                                </Text>
                                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                                    {ROSTER_STATES.map((status) => (
                                        <Text key={status} size="sm" muted>
                                            <StatusPill status={status} /> {STATUS_MEANING[status]}
                                        </Text>
                                    ))}
                                </div>
                            </>
                        )}
                    </ListCardRow>
                </ListCard>
            </Grid>
        </>
    );
}
